/**
 * Chat's full-results view (agora#269): every message `ChatClient.search`
 * (`@wtfalch/chat` 0.4.0) finds for a query, drawn by `SearchResultsPage`.
 * Reached at `/h/<agoraId>/chat/search/<query>`, which is what Enter opens
 * from the header search when nothing is highlighted (`SearchField.tsx`).
 *
 * Lives here rather than under `chat/` -- unlike `mail/MailSearchResults.tsx`
 * and `calendar/CalendarSearchResults.tsx`, which their own sections render
 * directly, this is mounted by `App.tsx`'s `AgoraView` in place of `Chat`
 * itself, so `chat/Chat.tsx` needs no changes for it to exist.
 *
 * Room-name and chat-people matches (`chat.ts`'s `matchRooms` and
 * `chatPeopleRows`) are left out: both are already synchronous, in-memory
 * and instantly actionable from the header panel itself, so there is
 * nothing a slower full page adds for them the way there is for a message
 * search capped at 20 rows.
 */
import { useEffect, useState } from 'react';
import type { ChatSearchClient } from './chat.ts';
import { searchChat } from './chat.ts';
import { chatErrorMessage } from './model.ts';
import { SearchResultsPage } from './SearchResultsPage.tsx';
import type { SearchRow } from './types.ts';

const LIMIT = 50;

export function ChatSearchResults({
  client,
  agoraId,
  query,
  onOpen,
}: {
  client: ChatSearchClient;
  agoraId: string;
  query: string;
  onOpen: (row: SearchRow) => void;
}) {
  const [rows, setRows] = useState<readonly SearchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    searchChat(client, agoraId, query, { limit: LIMIT })
      .then((outcome) => {
        if (!live) return;
        setRows(outcome.rows);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setError(chatErrorMessage(err));
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [client, agoraId, query]);

  return (
    <SearchResultsPage
      query={query}
      icon="chat"
      loading={loading}
      error={error}
      rows={rows}
      emptyLabel={`No messages found for “${query}”.`}
      onOpen={onOpen}
    />
  );
}
